<template>
  <div class="query-results-container">
    <Layout :class="{ 'white-background': whiteBackground }">
      <Content>
        <div class="query-table-container">
          <canvas
            ref="ruler"
            class="ruler"
          />
          <div class="query-table">
            <Tabs
              name="result-tabs"
              :value="tabActivated"
              :animated="false"
              @on-click="processTab"
            >
              <TabPane
                label="Table"
                name="table"
                tab="result-tabs"
              >
                <div
                  :style="{ height: `${tableHeight}px` }"
                  class="table-container"
                >
                  <CustomTable
                    :loading="loading"
                    :columns="columns"
                    :data="dataList"
                    :header-column="headerColumn"
                    :max-height="maxHeight"
                    :styles="tableStyles"
                    stripe
                    border
                  />
                </div>
                <div class="toolbar-container">
                  <Page
                    v-if="totalResults"
                    class-name="page-tabs"
                    placement="top"
                    :total="totalShown"
                    :current="page"
                    :page-size="pageSize"
                    :page-size-opts="pageSizeArr"
                    show-elevator
                    show-sizer
                    @on-change="changePage"
                    @on-page-size-change="changePageSize"
                  />
                  <div class="page-nav">
                    <span ref="queryTimer" />
                    <span
                      ref="resultCount"
                      style="margin-left: 8px"
                    />
                  </div>
                </div>
              </TabPane>
              <TabPane
                label="Charts"
                name="charts"
                tab="result-tabs"
                :disabled="!data.length"
              >
                <template v-show="tabActivated === 'charts'">
                  <ChartSection
                    v-model="charts"
                    :query-id="statementId"
                    :type-obj="columnTypeObj"
                    min-height="500px"
                  />
                </template>
              </TabPane>
              <TabPane
                label="Statistics"
                name="statistics"
                tab="result-tabs"
                :disabled="!data.length"
              >
                <StatSection
                  v-if="tabActivated === 'statistics'"
                  :statement-id="statementId"
                  :column-type-obj="columnTypeObj"
                />
              </TabPane>
              <TabPane
                label="Map"
                name="map"
                tab="result-tabs"
                :disabled="!data.length || !loadingMap"
              >
                <Layout v-if="tabActivated === 'map'">
                  <Content
                    :style="{ background: 'var(--color-white)', maxHeight: '523px' }"
                  >
                    <ChartCard
                      :data="data"
                      :show-charts="true"
                      :style="{ marginBottom: '10px' }"
                      title="Map"
                      chart-type="map"
                      selected
                    />
                  </Content>
                </Layout>
              </TabPane>
              <div slot="extra">
                <Dropdown
                  v-if="data.length && !fullDataPresent"
                  :transfer="true"
                  placement="bottom-end"
                  @on-click="handleExportMenuClick"
                >
                  <i class="fas fa-file-download action-button" />
                  <DropdownMenu slot="list">
                    <DropdownItem name="copyResult">
                      <i class="far fa-clipboard" /> Copy Current Result Batch
                    </DropdownItem>
                    <DropdownItem name="downloadAllData">
                      <i class="fas fa-download" /> Download
                    </DropdownItem>
                    <DropdownItem
                      name="googleSheet"
                      divided
                    >
                      <i class="fab fa-google-drive" /> Export to Google Sheets
                    </DropdownItem>
                    <DropdownItem name="googleScript">
                      <i class="fas fa-terminal" /> Export via Google Apps Script
                    </DropdownItem>
                    <DropdownItem
                      name="exportEmail"
                      divided
                    >
                      <i class="fas fa-envelope" /> Send to Email
                    </DropdownItem>
                    <DropdownItem name="exportSlack">
                      <i class="fab fa-slack" /> Send to Slack
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
                <i
                  v-if="!fullDataPresent"
                  class="fas fa-expand-arrows-alt action-button"
                  @click="showChartModal = true"
                />
              </div>
            </Tabs>
          </div>
        </div>
      </Content>
    </Layout>
    <Spin
      v-if="exporting"
      fix
    >
      <Icon
        type="ios-loading"
        size="18"
        class="demo-spin-icon-load"
      />
      <div>Exporting (Please note that export is limited to first 100,000 rows)</div>
    </Spin>
    <Modal
      v-model="showChartModal"
      :closable="false"
      :mask-closable="false"
      :footer-hide="true"
      class="modal"
      fullscreen
    >
      <Layout>
        <Content>
          <div class="query-table-container">
            <div class="query-table">
              <Tabs
                name="result-tabs"
                :value="tabActivated"
                :animated="false"
                @on-click="processTab"
              >
                <TabPane
                  label="Table"
                  name="table"
                  tab="result-tabs"
                >
                  <div
                    :style="{ height: `${tableHeight}px` }"
                    class="table-container"
                  >
                    <CustomTable
                      :loading="loading"
                      :columns="columns"
                      :data="dataList"
                      :header-column="headerColumn"
                      :max-height="maxHeight"
                      :styles="tableStyles"
                      border
                    />
                  </div>
                  <div class="toolbar-container">
                    <Page
                      v-if="totalResults"
                      class-name="page-tabs"
                      placement="top"
                      :total="totalShown"
                      :current="page"
                      :page-size="pageSize"
                      :page-size-opts="pageSizeArr"
                      show-elevator
                      show-sizer
                      @on-change="changePage"
                      @on-page-size-change="changePageSize"
                    />
                    <div class="page-nav">
                      <span ref="modalQueryTimer" />
                      <span
                        ref="modalResultCount"
                        style="margin-left: 8px"
                      />
                    </div>
                  </div>
                </TabPane>
                <TabPane
                  label="Charts"
                  name="charts"
                  tab="result-tabs"
                  :disabled="!data.length"
                >
                  <div v-show="tabActivated === 'charts'">
                    <ChartSection
                      v-model="charts"
                      :type-obj="columnTypeObj"
                      :query-id="statementId"
                      min-height="500px"
                    />
                  </div>
                </TabPane>
                <TabPane
                  label="Statistics"
                  name="statistics"
                  tab="result-tabs"
                  :disabled="!data.length"
                >
                  <StatSection
                    v-if="tabActivated === 'statistics'"
                    :statement-id="statementId"
                    :column-type-obj="columnTypeObj"
                  />
                </TabPane>
                <TabPane
                  label="Map"
                  name="map"
                  tab="result-tabs"
                  :disabled="!data.length || !loadingMap"
                >
                  <Layout v-if="tabActivated === 'map' && showChartModal">
                    <Content
                      :style="{ background: 'var(--color-white)', maxHeight: '523px' }"
                    >
                      <ChartCard
                        :data="data"
                        :show-charts="true"
                        :style="{ marginBottom: '10px' }"
                        title="Map"
                        chart-type="map"
                        selected
                      />
                    </Content>
                  </Layout>
                </TabPane>
                <div slot="extra">
                  <Dropdown
                    v-if="data.length"
                    :transfer="true"
                    placement="bottom-end"
                    @on-click="handleExportMenuClick"
                  >
                    <i class="fas fa-file-download action-button" />
                    <DropdownMenu slot="list">
                      <DropdownItem name="copyResult">
                        <i class="far fa-clipboard" /> Copy Current Result Batch
                      </DropdownItem>
                      <DropdownItem name="downloadAllData">
                        <i class="fas fa-download" /> Download
                      </DropdownItem>
                      <DropdownItem
                        name="googleSheet"
                        divided
                      >
                        <i class="fab fa-google-drive" /> Export to Google Sheets
                      </DropdownItem>
                      <DropdownItem name="googleScript">
                        <i class="fas fa-terminal" /> Export via Google Apps Script
                      </DropdownItem>
                      <DropdownItem
                        name="exportEmail"
                        divided
                      >
                        <i class="fas fa-envelope" /> Send to Email
                      </DropdownItem>
                    </DropdownMenu>
                  </Dropdown>
                  <i
                    class="fas fa-times action-button"
                    style="font-size: 1.5em"
                    @click="showChartModal = false"
                  />
                </div>
              </Tabs>
            </div>
          </div>
        </Content>
      </Layout>
    </Modal>
    <QueryEmailDrawer
      :context="context"
      :query-name="queryShareName"
      :query-results="data"
      :col-title-arr="colTitleArr"
      :is-composing-email="composingEmail"
      @isComposingEmail="onEmailComposing"
    />
    <QuerySlackDrawer
      :context="context"
      :query-name="queryShareName"
      :query-results="data"
      :query-id="queryId"
      :users="slackUsers"
      :channels="slackChannels"
      :col-title-arr="colTitleArr"
      :is-exporting-via-slack="exportingViaSlack"
      @isExportingViaSlack="onExportingViaSlack"
    />
  </div>
</template>

<script>
import ChartCard from './ChartCard'
import QueryEmailDrawer from './QueryEmailDrawer'
import QuerySlackDrawer from './QuerySlackDrawer'
import CustomTable from './dashboard-components/CustomTable'
import ChartSection from '@/components/helper-components/query-table/ChartSection'
import StatSection from '@/components/helper-components/query-table/StatSection'

import axios from 'axios'
import _ from 'lodash'

export default {

  components: {
    ChartCard,
    QueryEmailDrawer,
    QuerySlackDrawer,
    CustomTable,
    ChartSection,
    StatSection
  },

  props: {
    queryId: Number,
    query: String,
    queryName: String,
    index: Number,
    results: {
      type: Array,
      default: () => []
    },
    hasQueryRun: Boolean,
    limit: Number,
    snowflakeConfig: Object,
    context: String,
    showAction: Boolean, // Boolean for download button show/hide
    sharedQueryFileName: String,
    initialStatementId: String,
    whiteBackground: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    shareMode: {
      type: Boolean,
      default: false
    },
    fullDataPresent: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      loading: this.isLoading, // Loading screen boolean.
      columns: {}, // Holds the column configuration object used by CustomTable
      data: [], // All of the queried data.
      columnTypeObj: {}, // Object storing data types for columns
      queryLimit: this.limit || 100000, // Limit for the number of results returned.
      totalResults: 0, // Total number of query results.
      totalShown: 0, // Total number of rows for shown data.
      page: 1, // Current page.
      pageSize: 100, // Number of rows per page.
      pageSizeArr: [10, 50, 100], // The options for pageSize.
      batchSize: 1000, // Number of rows per batch.
      currentBatchStartPageNum: 1, // Min page number for current batch
      currentBatchEndPageNum: 10, // Max page number for current batch
      maxHeight: 430, // Height of table.
      maxColWidth: 350, // Maximum width of column before expanding.
      colWidthPadding: 50, // Side padding for column titles so there is no overflow.
      colWidthMap: new Map(), // The widths for the columns to expand in order to show full data.
      colButtonWidth: 24, // The width of the column expand button plus margin.
      colSiderWidth: 200, // Initial width for column tags
      colSiderMinWidth: 200, // Minimum width for column tags
      colTagPadding: 30, // The width of the column tag to avoid overflow
      queryTimerId: null, // Id for the queryTimer in setInterval.
      queryTime: null, // The time at which the last query ran.
      showDashboard: false, // Loading dashboard boolean
      showChartModal: false, // Loading chart modal boolean
      dashboardTitle: '',
      dashboardType: '',
      urlBase: '',
      urlPreview: '',
      exporting: false, // Spin show boolean
      composingEmail: false, // Composing email drawer show boolean
      selectedColumns: [], // Array stores selected columns using boolean
      loadingMap: false, // Loading map boolean
      tabActivated: 'table', // Default tab to be shown
      queryShareName: this.queryName,
      exportingViaSlack: false,
      slackUsers: [],
      slackChannels: [],
      statementId: this.initialStatementId || '',
      headerColumn: {},
      tableStyles: {},
      charts: []
    }
  },

  computed: {
    dataList () {
      const start = (this.page - 1) % (this.batchSize / this.pageSize) * this.pageSize
      const end = start + this.pageSize
      return this.data.slice(start, end).map((item, index) => {
        return Object.freeze({ ...item, _index: (this.page - 1) * this.pageSize + 1 + index })
      })
    },

    colTitleArr () {
      return Object.keys(this.data.length === 0 ? {} : this.data[0])
    },

    tableHeight () {
      const tableHeight = this.dataList.length * 30 + 32
      return tableHeight < this.maxHeight ? tableHeight : this.maxHeight
    }
  },

  watch: {
    /**
     * Runs the SQL query input in Snowflake and displays the results in the table.
     *
     * It will automatically run with every new SQL query input by using a POST
     * request on the /snowflake-queries endpoint with the query as data. If an error
     * occurs, the error is displayed.
     *
     * The query results currently have a hard limit of 100. This is due to large
     * query results using up too much server memory. Thus, limit/offset pagination
     * will be implemented in the future.
     *
     * @param {string} newQuery The new input value for query.
     */
    query (newQuery) {
      if (!this.shareMode) {
        this.runQuery(newQuery, true)
      }
    },

    results (newResults) {
      if (this.shareMode) {
        this.processResults(newResults)
      }
    },

    isLoading (newIsLoading) {
      this.loading = newIsLoading
    },

    queryName (newQueryName) {
      this.queryShareName = newQueryName
    }
  },

  mounted () {
    if (this.query && !this.hasQueryRun && !this.shareMode) {
      this.runQuery(this.query)
    } else {
      this.processResults(this.results)
    }
  },

  methods: {
    /**
     * Resizes the columns according to the size of the screen.
     *
     * It aims to allocate the most space for column width by determing whether the
     * column header or the data entry in the current row is larger. If the largest
     * width is greater than the maximum column width, the maximum column width is
     * set. The largest widths are stored in colWidthMap.
     *
     * @param {Array} columns Holds the column info.
     * @param {Object} data Holds the table data
     */
    setColumnWidths (columns, data) {
      Object.keys(columns).forEach(key => {
        const col = columns[key]
        if (col.hasOwnProperty('type')) {
          col.width = this.measureColumnWidth(this.data.length) + this.colWidthPadding
          return
        }
        let longestLength = col.displayName.toString().length
        let dataWidth = this.measureColumnWidth(col.displayName) + this.colButtonWidth
        data.forEach(row => {
          const dataString = row[col.name] ? row[col.name].toString() : ''
          if (dataString.length >= longestLength) {
            const curDataWidth = this.measureColumnWidth(row[col.name])
            if (curDataWidth > dataWidth) {
              dataWidth = curDataWidth
              longestLength = dataString.length
            }
          }
        })
        dataWidth += this.colWidthPadding
        this.colWidthMap[key] = dataWidth
        col.minWidth = dataWidth > this.maxColWidth ? this.maxColWidth : dataWidth
      })
      return columns
    },

    /**
     * Calculate col tag sider width
     * @param {Array} columns Holds the column info.
     */
    setColumnTagWidth (columns) {
      columns.forEach(col => {
        const dataWidth = this.measureColumnWidth(col.name) + this.colTagPadding
        if (this.colSiderWidth < dataWidth) {
          this.colSiderWidth = dataWidth
        }
      })

      if (this.colSiderWidth < this.colSiderMinWidth) {
        this.colSiderWidth = this.colSiderMinWidth
      }
    },

    /**
     * Creates the columns for the table.
     * @param {Object} data All of the data results.
     */
    fetchColumns (data) {
      const newColumns = {}

      this.selectedColumns = this.colTitleArr.map(title => (
        {
          title: title,
          selected: false,
          chartType: 'bar'
        }
      ))

      this.colTitleArr.forEach(title => {
        if (title === '_index') {
          return
        }

        newColumns[title] = {
          name: title,
          displayName: title,
          format: 'string',
          ellipsis: true,
          renderHeader: (h, params) => {
            if (params.column.minWidth < this.maxColWidth) {
              return h('span', params.column.displayName || params.column.name)
            }
            return h('div', [
              h('span', params.column.displayName || params.column.name),
              h('Tooltip', {
                props: {
                  content: 'Maximize/Minimize Column Width'
                },
                style: {
                  'margin-left': '8px'
                }
              }, [
                h('Icon', {
                  props: {
                    type: 'md-swap',
                    size: '16'
                  },
                  style: {
                    cursor: 'pointer'
                  },
                  on: {
                    click: async () => {
                      this.$nextTick(() => {
                        this.loading = true
                      })
                      await this.resizeColumnWidth(params.column.name, this.columns, this.maxColWidth, this.colWidthMap)
                      this.$nextTick(() => {
                        this.loading = false
                      })
                    }
                  }
                })
              ])
            ])
          }
        }
      })

      this.setColumnTagWidth(this.selectedColumns)
      return this.setColumnWidths(newColumns, data)
    },

    /**
     * Expands or contracts the column.
     * @param {String} key table columns selected key
     * @param {Object} columns table columns.
     * @param {int} maxColWidth The maximum initial column width.
     * @param {Array} colWidthMap The array of values that store the largest width per column.
     */
    resizeColumnWidth (key, columns, maxColWidth, colWidthMap) {
      return new Promise(resolve => {
        this.$nextTick(() => {
          const curWidth = columns[key].minWidth
          if (curWidth === maxColWidth) {
            columns[key].minWidth = colWidthMap[key]
          } else if (curWidth > maxColWidth) {
            columns[key].minWidth = maxColWidth
          }
          resolve()
        })
      })
    },

    /**
     * Changes the number of rows shown on a single page.
     * @param {int} pageSize The number of rows per page.
     */
    changePageSize (pageSize) {
      this.pageSize = pageSize
      this.currentBatchStartPageNum = 1
      this.currentBatchEndPageNum = this.batchSize / this.pageSize
      if (this.page !== 1) {
        this.changePage(1)
      } else {
        this.runQuery(this.query, false)
      }
    },

    /**
     * Changes the current page on the table.
     * @param {int} page The current page.
     */
    changePage (page) {
      this.page = page
      if (!this.fullDataPresent && (this.page < this.currentBatchStartPageNum || this.page > this.currentBatchEndPageNum)) {
        this.currentBatchStartPageNum = 1 + Math.floor(this.page / (this.batchSize / this.pageSize)) * (this.batchSize / this.pageSize)
        this.currentBatchEndPageNum = (Math.floor(this.page / (this.batchSize / this.pageSize)) + 1) * (this.batchSize / this.pageSize)
        this.runQuery(this.query, false)
      }
    },

    /**
     * Returns the width in the number of pixels.
     *
     * It gets the value by placing the input into a canvas element and then
     * calculating the width.
     *
     * @param {string} word The value to be measured.
     */
    measureColumnWidth (word) {
      const ruler = this.$refs.ruler
      let context = ruler.getContext('2d')
      context.font = '12px Montserrat, sans-serif'
      const metrics = context.measureText(word)
      return metrics.width
    },

    /**
     * Exports the table in different ways
     * @param {String} type Determines how to export the data.
     */
    exportData (type) {
      this.exporting = true
      const timeOfDownload = this.$moment().format('YYYY-MM-DD_HH-mm-ss')
      const filename = `${this.queryShareName}_${timeOfDownload}`

      if (type === 'appscript') {
        return this.saveSheet(this.data, filename, 'appscript')
      } else if (type === 'clipboard') {
        this.$log('result_copy', this.query)
        const header = Object.keys(this.data[0]).join('\t')
        const body = this.data.map(row => Object.values(row).join('\t')).join('\n')
        return this.$copyText(`${header}\n${body}`).then(
          this.onCopySuccess.bind(this),
          this.onCopyError.bind(this)
        ).finally(() => { this.exporting = false })
      }

      if (this.shareMode) {
        if (type === 'csvAll') {
          this.$log('result_download', this.query, { title: filename })
          this.exportCsv(filename)
          this.exporting = false
        } else if (type === 'spreadsheet') {
          this.$log('result_sheet_export', this.query, { title: filename })
          this.saveSheet(this.data, filename, 'spreadsheet')
        } else if (type === 'email') {
          this.$log('result_email', this.query, { title: filename })
          this.exporting = false
          this.composingEmail = true
        }
        return
      }

      if (type === 'slack') {
        Promise.all([axios.get(process.env.VUE_APP_API_URI + '/slack/users'), axios.get(process.env.VUE_APP_API_URI + '/slack/channels')]).then(([users, channels]) => {
          this.slackUsers = users.data
          this.slackChannels = channels.data
          this.exportingViaSlack = true
          this.exporting = false
        }).catch((error) => {
          this.$Message.error(`Failed to fetch slack channels: ${error}`)
        })
        return
      }

      this.$log('query_run_for_export', this.query)

      axios({
        method: 'post',
        url: process.env.VUE_APP_API_URI + '/snowflake/queries/stream',
        timeout: 1000 * 60 * 5, // wait for 5 minutes
        headers: {
          'Content-Type': 'application/json'
        },
        data: {
          query: this.query,
          limit: this.queryLimit,
          snowflakeConfig: this.snowflakeConfig || null
        }
      }).then(results => {
        if (results.data.hasOwnProperty('error')) {
          this.exporting = false
          this.$Message.error(`${results.data.error.name}: ${results.data.error.message}`)
        } else {
          this.data = results.data.map(item => {
            return Object.freeze(item)
          })
          if (type === 'csvAll') {
            this.$log('result_download', this.query, { title: filename })
            this.exportCsv(filename)
            this.exporting = false
          } else if (type === 'spreadsheet') {
            this.$log('result_sheet_export', this.query, { title: filename })
            this.saveSheet(this.data, filename, 'spreadsheet')
          } else if (type === 'email') {
            this.$log('result_email', this.query, { title: filename })
            this.exporting = false
            this.composingEmail = true
          }
        }
      }).catch(error => {
        console.error(error)
        this.exporting = false
        this.$Message.error(`${Error}: ${error.message}`)
      })
    },

    /**
     * Escapes certain characters from string values to prevent faulty formatting when value is exported
     * into CSV file
     * @param {any} value
     * @return {any}
     */
    csvStringEscape (value) {
      if (typeof value === 'string') {
        const formattedValue = value.replace(/\n/g, ' ')
        return formattedValue.includes(',') ? `"${formattedValue}"` : formattedValue
      }

      return value
    },

    /**
     * Exports the table data into a csv
     * @param {String} filename - The name of the file to be saved
     */
    exportCsv (filename) {
      const header = Object.keys(this.data[0]).map(field => this.csvStringEscape(field)).join(',')
      const body = this.data.map(row => Object.values(row).map(value => this.csvStringEscape(value)).join(',')).join('\n')
      const file = new Blob([[header, body].join('\n')], { type: 'text/csv' })
      const fileLink = document.createElement('a')
      fileLink.href = window.URL.createObjectURL(file)
      fileLink.setAttribute('download', `${filename}.csv`)
      document.body.appendChild(fileLink)
      fileLink.click()
      document.body.removeChild(fileLink)
    },

    /**
     * Displays a success message if the copy/cut to clipboard worked.
     */
    onCopySuccess () {
      this.$Message.success({
        content: `Copied ${this.data.length} rows to clipboard!`,
        duration: 3
      })
    },

    /**
     * Displays an error message if the copy/cut to clipboard didn't worked.
     */
    onCopyError () {
      this.$Message.error({
        content: `Error while copying to clipboard!`,
        duration: 3
      })
    },

    /**
     * Initializes logged in gapi client, then convert and save sheet to Google Sheet
     * @param {Array} data Raw table data to be exported to Google Sheet
     * @param {String} filename Used for naming
     * @param {String} mode Export to Google Sheet or Google Apps Script
     */
    async saveSheet (data, filename, mode) {
      try {
        const gapi = await this.$getGapiClient()
        if (!this.$isAuthenticated()) {
          this.$refreshToken()
        }

        if (!await gapi.auth2.getAuthInstance().currentUser.get().isSignedIn()) {
          await this.$login()
        }

        if (mode === 'spreadsheet') {
          const sheet = this.convertDataToSheet(data, filename)
          const request = gapi.client.sheets.spreadsheets.create(sheet)
          request.then(
            (response) => this.onSheetCreationSuccess(response.result.properties.title, response.result.spreadsheetUrl),
            (error) => this.onSheetCreationError(error.result.error.message)
          )
        } else if (mode === 'appscript') {
          const sheet = await gapi.client.drive.files.copy({
            fileId: process.env.VUE_APP_GOOGLE_SHEET_ID,
            name: filename
          })
          const request = gapi.client.sheets.spreadsheets.values.update({
            spreadsheetId: sheet.result.id,
            valueInputOption: 'RAW',
            range: 'Console!A4:A4',
            majorDimension: 'ROWS',
            values: [[this.query]]
          })
          request.then(
            () => this.onSheetCreationSuccess(
              sheet.result.name,
              `https://docs.google.com/spreadsheets/d/${sheet.result.id}`),
            (error) => this.onSheetCreationError(error.result.error.message)
          )
        }
      } catch (error) {
        let errorMessage
        if (error.error) {
          errorMessage = error.error
        } else if (error.result && error.result.error) {
          errorMessage = error.result.error.message
        } else {
          errorMessage = error
        }
        return this.onSheetCreationError(errorMessage)
      }
    },

    /**
     * This converts table data to Google Sheet API complied format
     * @param {Array} data Represents the table data from query.
     * @param {String} filename used for naming
     * @return {Array} Converted array in Google Sheet compatible format
     */
    convertDataToSheet (data, filename) {
      const rowValue = data.map(row => {
        const values = Object.values(row).map(each => {
          if (typeof each === 'number') {
            return {
              userEnteredValue: {
                numberValue: each
              }
            }
          } else if (typeof each === 'boolean') {
            return {
              userEnteredValue: {
                boolValue: each
              }
            }
          } else if (Array.isArray(each)) {
            return {
              userEnteredValue: {
                stringValue: `[${each.toString()}]`
              }
            }
          } else {
            return {
              userEnteredValue: {
                stringValue: each
              }
            }
          }
        })
        return {
          values: values
        }
      })

      const rowHead = Object.keys(data[0]).map(each => {
        return {
          userEnteredValue: {
            stringValue: each
          }
        }
      })

      const rows = [{ values: rowHead }, ...rowValue]

      const sheet = {
        properties: {
          title: filename
        },
        sheets: [{
          data: [{
            rowData: rows
          }]
        }]
      }

      return sheet
    },

    /**
     * Callback function to invoke dashboard open function
     * @param {String} title Represents Google Spreadsheet title
     * @param {String} url Represents Google Spreadsheet URL
     */
    onSheetCreationSuccess (title, url) {
      this.exporting = false
      window.open(url, '_blank')
    },

    /**
     * Displays a pop up to show sheet export error
     * @param {String} error represents error message to display.
     */
    onSheetCreationError (error) {
      this.exporting = false
      this.$Message.error({
        content: `Error while exporting to Google Sheet because ${error}`,
        duration: 8
      })
    },

    /**
     * Executes show/hide Email Composing Drawer
     * @param {Boolean} value represents updated value from QueryEmailDrawer for show/hide
     */
    onEmailComposing (value) {
      this.composingEmail = value
    },

    /**
     * Executes show/hide for slack export drawer
     * @param {Boolean} value represents updated value from SlackEmailDrawer for show/hide
     */
    onExportingViaSlack (value) {
      this.exportingViaSlack = value
    },

    /**
     * This updates the queryTimer span to display the elapsed time since the last query was ran.
     */
    queryTimer () {
      const currentTime = new Date().getTime()
      const duration = currentTime - this.queryTime
      const minutes = Math.floor(duration / (1000 * 60))
      const seconds = Math.floor((duration % (1000 * 60)) / 1000)

      let queryTime = ''
      if (minutes) {
        queryTime += minutes + 'm '
      }
      queryTime += seconds + 's'

      this.$refs.queryTimer.innerHTML = queryTime
      this.$refs.modalQueryTimer.innerHTML = queryTime
    },

    /**
     * Checks what type of chart that needs to display
     */
    processChart () {
      let selectedLat = 0
      let selectedLng = 0

      this.selectedColumns.forEach(column => {
        if (column.title.includes('LAT')) {
          ++selectedLat
        }
        if (column.title.includes('LONG') || column.title.includes('LNG')) {
          ++selectedLng
        }
      })

      this.loadingMap = [selectedLat, selectedLng].every(value => value > 0)
    },

    /**
     * Records current activated tab name
     * @param {String} name Tab name
     */
    processTab (name) {
      this.tabActivated = name
    },

    /**
     * Executes the provided query.
     * @param {String} query query string to be run
     * @param {Boolean} isNewQuery to determine if this is a new query or new batch on existing query
     */
    async runQuery (query, isNewQuery) {
      if (this.loading) {
        return
      }

      if (query.trim().length === 0) {
        this.data = []
        this.columnTypeObj = {}
        return
      }

      this.loading = true

      // Reset the time, data, and columns on a query update.
      // No need to run this for shareMode as we cannot change the query.
      if (isNewQuery) {
        this.queryTime = new Date().getTime()
        this.queryTimerId = setInterval(this.queryTimer, 1000)
        this.$refs.resultCount.innerHTML = ''
        this.$refs.modalResultCount.innerHTML = ''
        this.selectedColumns = []
        this.data = []
        this.columnTypeObj = {}
      }
      this.tabActivated = 'table'

      this.runSnowflakeQueries(query, isNewQuery)
    },

    runSnowflakeQueries (query, isNewQuery) {
      axios({
        method: 'post',
        url: process.env.VUE_APP_API_URI + '/snowflake/queries',
        timeout: 1000 * 60 * 5, // wait for 5 minutes
        headers: {
          'Content-Type': 'application/json'
        },
        data: {
          query: query,
          limit: this.queryLimit,
          snowflakeConfig: this.snowflakeConfig || null,
          offset: isNewQuery ? 0 : Math.floor((this.page - 1) / (this.batchSize / this.pageSize)) * this.batchSize,
          batchSize: this.batchSize,
          email: isNewQuery ? this.$auth.getUser().email : null,
          statementId: (this.shareMode || isNewQuery) ? null : this.statementId,
          isSharedQuery: this.shareMode
        }
      })
        .then(results => {
          if (results.data.hasOwnProperty('error')) {
            this.onQueryError(results)
          } else {
            this.onQuerySuccess(results, isNewQuery)
          }
        })
        .catch(this.onQueryErrorCatch.bind(this))
        .finally(() => {
          if (isNewQuery) {
            clearInterval(this.queryTimerId)
          }

          this.loading = false
          this.$emit('runningQuery', this.index, false)
        })
    },

    /**
     * Runs when there is an error while running the query
     * @param {Object} results - The result of the query API call
     */
    onQueryError (results) {
      this.headerColumn = {}
      this.tableStyles = {
        leftColWidth: 0
      }
      this.columns = {
        error: {
          displayName: this.shareMode ? 'Cannot Get Shared Query' : results.data.error.name
        }
      }
      this.data = [
        {
          error: this.shareMode ? 'Shared Results Link might be invalid or expired.' : results.data.error.message
        }
      ]
      this.columnTypeObj = {}
      this.totalShown = 1
      this.totalResults = 0
      this.$refs.resultCount.innerHTML = ''
      this.$refs.modalResultCount.innerHTML = ''
      this.pageSize = 100
      this.page = 1
      this.statementId = ''
      this.$emit('outcome', this.index, this.data, this.statementId)
    },

    /**
     * Runs when the query runs successfully
     * @param {Object} results - The result from the query request
     * @param {Boolean} isNewQuery - True if the query being ran is new. False if not - we are simply asking for
     * the next batch
     */
    onQuerySuccess (results, isNewQuery) {
      this.headerColumn = {
        name: '_index',
        displayName: '#',
        format: 'number'
      }
      this.tableStyles = {
        leftColWidth: 62
      }
      this.data = results.data.data
      Object.freeze(this.data)
      this.columnTypeObj = _.cloneDeep(results.data.types)
      this.totalResults = results.data.total

      if (isNewQuery) {
        this.statementId = results.data.queryId
        this.columns = this.data.length === 0 ? {} : this.fetchColumns(this.dataList) // Potentially buggy
        this.processChart()
        this.$emit('outcome', this.index, this.data, this.statementId)
        this.pageSize = 100
        this.page = 1

        // TODO: fix this.dataList will using this.page and this.pageSize on new results
        this.totalShown = results.data.total > this.limit ? this.limit : results.data.total
        const commaRegex = /\B(?=(\d{3})+(?!\d))/g
        const totalShownString = `(${this.totalShown.toString().replace(commaRegex, ',')} shown)`
        const totalResultsString = this.totalResults.toString().replace(commaRegex, ',') + ' rows'
        const resultHtml = this.totalShown === this.totalResults ? totalResultsString : `${totalResultsString} ${totalShownString}`
        this.$refs.resultCount.innerHTML = resultHtml
        this.$refs.modalResultCount.innerHTML = resultHtml
      }
    },

    /**
     * Runs when there is an error thrown when query is being run
     * @param {Object} error - The error object
     */
    onQueryErrorCatch (error) {
      this.headerColumn = {}
      this.tableStyles = {
        leftColWidth: 0
      }
      this.columns = {
        error: {
          displayName: 'Error'
        }
      }
      this.data = [
        {
          error: error.message
        }
      ]
      this.columnTypeObj = {}
      this.$log('query_timeout', this.queryName, { query: this.query })
      this.totalShown = 1
      this.totalResults = 0
      this.$refs.resultCount.innerHTML = ''
      this.$refs.modalResultCount.innerHTML = ''
      this.pageSize = 100
      this.page = 1
      this.statementId = ''
      this.$emit('outcome', this.index, this.data, this.statementId)
    },

    /**
     * Process results data
     * @param {Array} results
     */
    processResults (results) {
      if (!results.length) {
        return
      }
      this.tabActivated = 'table'
      this.data = results
      this.totalResults = results.length
      this.headerColumn = {
        name: '_index',
        displayName: '#',
        format: 'number'
      }
      this.tableStyles = {
        leftColWidth: 62
      }
      this.columns = this.data.length === 0 ? {} : this.fetchColumns(this.dataList) // Potentially buggy
      this.processChart()
      // TODO: fix this.dataList will using this.page and this.pageSize on new results
      this.totalShown = results.length > this.limit ? this.limit : results.length
      const commaRegex = /\B(?=(\d{3})+(?!\d))/g
      const totalShownString = `(${this.totalShown.toString().replace(commaRegex, ',')} shown)`
      const totalResultsString = this.totalResults.toString().replace(commaRegex, ',') + ' rows'
      const resultHtml = this.totalShown === this.totalResults ? totalResultsString : `${totalResultsString} ${totalShownString}`
      this.$refs.resultCount.innerHTML = resultHtml
      this.$refs.modalResultCount.innerHTML = resultHtml
    },

    /**
     * Handle export event on menu
     */
    handleExportMenuClick (action) {
      switch (action) {
        case 'copyResult':
          this.exportData('clipboard')
          break
        case 'downloadAllData':
          this.exportData('csvAll')
          break
        case 'googleSheet':
          this.exportData('spreadsheet')
          break
        case 'googleScript':
          this.exportData('appscript')
          break
        case 'exportEmail':
          this.exportData('email')
          break
        case 'exportSlack':
          this.exportData('slack')
          break
        default:
          break
      }
    }
  }
}
</script>

<style>
  .ivu-layout .white-background {
    background-color: var(--color-white);
  }

  .query-results-container {
    position: relative;
    border-left: 1px solid var(--color-gray-border);
    background-color: var(--color-body-background);
  }

  .query-table-container {
    position: relative;
  }

  .query-table {
    transition: all 0.3s ease;
  }

  .toolbar-container {
    display: flex;
    justify-content: space-between;
    padding: 8px 0;
    font-size: 12px;
    background-color: var(--color-body-background);
  }

  .ivu-layout .white-background .toolbar-container {
    background-color: var(--color-white);
  }

  .ivu-layout .white-background .ivu-spin-fix {
    border-right: 1px solid var(--color-card-border);
    border-bottom: 1px solid var(--color-card-border);
  }

  .page-tabs {
    padding-left: 8px;
  }

  .page-nav {
    display: flex;
    align-items: baseline;
    padding-right: 8px;
    line-height: 32px;
  }

  .ruler {
    position: absolute;
    visibility: hidden;
    white-space: nowrap;
  }

  .table-buttons-container {
    padding-left: 8px;
  }

  .table-buttons-container i {
    max-width: 16px;
    min-width: 16px;
    height: 16px;
    margin-right: 8px;
    padding: 0;
    width: 16px;
    display: inline-block;
    font-size: 14px;
    font-style: normal;
    font-variant: normal;
    text-transform: none;
    text-rendering: auto;
    text-align: center;
    line-height: 1;
    vertical-align: middle;
    color: var(--color-black);
  }

  .query-sider {
    background: var(--color-white);
    max-height: 530px;
    overflow-y: auto;
    border: 1px solid var(--color-gray-border);
    border-left: none;
  }

  .query-sider.ivu-layout-sider-collapsed {
    border-right: none;
  }

  .demo-spin-icon-load {
    animation: ani-demo-spin 1s linear infinite;
  }

  .modal >>> .ivu-modal-close .ivu-icon {
    font-weight: 700;
  }

  .item-cell-inner span {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  .sort {
    line-height: 20px;
    border-bottom: 1px solid var(--color-gray-border);
    font-weight: bold;
    font-size: 14px;
    padding: 0 8px;
    margin-bottom: 5px;
  }

  .shared-table-divider {
    margin: 0;
  }

  .ivu-modal-fullscreen-no-footer .ivu-modal-body {
    background-color: var(--color-body-background);
  }

  .action-button {
    padding: 10px 7px 2px;
    font-size: 1.3em;
    cursor: pointer;
  }
</style>
